import { adminAuditEntrySchema, type AdminAuditEntry } from '@steado/admin-contract/activity';
import { paginationMetaSchema, type PaginationMeta } from '@steado/admin-contract/pagination';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import type { ApiClient, QueryValue } from '@/lib/api/client';

export const ACTIVITY_PAGE_SIZE = 25;

export interface ActivityFilters {
  readonly action?: string | undefined;
  readonly entity?: string | undefined;
  readonly entityId?: string | undefined;
  readonly actorUserId?: string | undefined;
  readonly scope?: string | undefined;
  readonly cursor?: string | undefined;
}

export interface ActivityPage {
  readonly items: AdminAuditEntry[];
  readonly pagination: PaginationMeta;
}

const activityPageSchema = z.object({
  items: z.array(adminAuditEntrySchema),
  pagination: paginationMetaSchema,
});

/**
 * Read-only, and there is no mutation to pair it with: the audit trail is
 * written by the actions it records, inside their own transactions. A console
 * that could append to it would be a way to forge a row.
 */
export function activityQueryOptions(api: ApiClient, filters: ActivityFilters) {
  const query: Record<string, QueryValue> = {
    action: filters.action,
    entity: filters.entity,
    entityId: filters.entityId,
    actorUserId: filters.actorUserId,
    scope: filters.scope,
    cursor: filters.cursor,
    limit: ACTIVITY_PAGE_SIZE,
  };

  return queryOptions({
    queryKey: ['admin', 'activity', filters],
    queryFn: async (): Promise<ActivityPage> => {
      const response = await api.requestWithMeta({
        path: '/admin/activity',
        query,
        schema: z.array(adminAuditEntrySchema),
      });

      return activityPageSchema.parse({ items: response.data, pagination: response.meta.pagination });
    },
    placeholderData: (previous) => previous,
  });
}
